import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAssetById, triggerAction } from '../services/assetService';
import type { AssetEvent } from '../types/asset';
import { Asset } from '../types/asset';
import { StatusBadge } from '../components/shared/StatusBadge';
import { FileTypeBadge } from '../components/shared/FileTypeBadge';
import { formatFileSize, formatDate, formatRelativeTime, extractFileName } from '../utils/formatters';
import { resources } from '../resources';
import { ArrowLeft, Copy, Download, Clock, Info, Share2, CheckCircle, Database } from 'lucide-react';

function isStateChangeEvent(event: AssetEvent): event is Extract<AssetEvent, { type?: 'STATE_CHANGE' }> {
  return !event.type || event.type === 'STATE_CHANGE';
}

export const AssetDetail: React.FC = () => {
  const { assetId } = useParams();
  const [asset, setAsset] = useState<Asset | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [actionState, setActionState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [actionMessage, setActionMessage] = useState<string>('');
  const navigate = useNavigate();

  const handleAction = async (actionType: string) => {
    if (!assetId) return;
    setActionState('loading');
    setActionMessage('');
    try {
      const result = await triggerAction(assetId, actionType);
      setActionState('success');
      setActionMessage(`Re-ingest triggered (ID: ${result.actionId})`);
      setTimeout(() => setActionState('idle'), 4000);
    } catch {
      setActionState('error');
      setActionMessage('Failed to trigger action. Please try again.');
      setTimeout(() => setActionState('idle'), 4000);
    }
  };

  useEffect(() => {
    if (assetId) {
      getAssetById(assetId).then(a => setAsset(a ?? null));
    }
  }, [assetId]);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  if (!asset) return <div className="p-8 text-center text-muted-foreground">{resources.common.loading}</div>;

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-12">
      <button
        onClick={() => navigate('/assets')}
        className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {resources.common.backToAssets}
      </button>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left Column: Metadata & Core Details */}
        <div className="lg:w-2/3 space-y-8">
          <div className="bg-card rounded-3xl border border-border shadow-sm overflow-hidden">
             <div className="p-8 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <h1 className="text-3xl font-bold text-foreground tracking-tight">{extractFileName(asset.locationPath)}</h1>
                    </div>
                    <div className="flex items-center gap-3">
                      <FileTypeBadge type={asset.fileType} />
                      <StatusBadge status={asset.state} />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="p-2 border border-border rounded-xl hover:bg-muted text-muted-foreground">
                      <Share2 className="w-5 h-5" />
                    </button>
                    <button className="flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-xl font-semibold hover:opacity-90 transition-all text-sm shadow-lg">
                      <Download className="w-4 h-4" />
                      {resources.common.metadataJson}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-6 border-t border-border">
                  <div className="space-y-1">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{resources.assetDetail.labels.size}</p>
                    <p className="text-lg font-semibold text-foreground">{formatFileSize(asset.size)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{resources.assetDetail.labels.received}</p>
                    <p className="text-lg font-semibold text-foreground">{formatRelativeTime(asset.receivedOn)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{resources.assetDetail.labels.storage}</p>
                    <p className="text-lg font-semibold text-foreground">{asset.storageType}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{resources.assetDetail.labels.events}</p>
                    <p className="text-lg font-semibold text-foreground">{asset.events.length}</p>
                  </div>
                </div>
             </div>

             <div className="bg-muted border-t border-border p-8 space-y-6">
               <div className="space-y-4">
                 <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                   <Database className="w-4 h-4 text-muted-foreground" />
                   {resources.assetDetail.storageInfo}
                 </h3>
                 <div className="space-y-4">
                    <div className="group">
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5">{resources.assetDetail.sourceUri}</p>
                      <div className="flex items-center gap-2 bg-card border border-border rounded-xl p-3 shadow-sm">
                        <code className="text-xs font-mono text-primary break-all flex-1">{asset.sourceUri}</code>
                        <button
                          onClick={() => handleCopy(asset.sourceUri, 'source')}
                          className="p-1.5 hover:bg-primary/10 rounded-lg text-muted-foreground hover:text-primary transition-all"
                        >
                          {copied === 'source' ? <CheckCircle className="w-4 h-4 text-secondary" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {asset.stagedCopy && (
                      <div className="group">
                        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5">{resources.assetDetail.stagedUri}</p>
                        <div className="flex items-center gap-2 bg-secondary/10 border border-secondary/20 rounded-xl p-3 shadow-sm">
                          <code className="text-xs font-mono text-secondary break-all flex-1">{asset.stagedCopy}</code>
                          <button
                             onClick={() => handleCopy(asset.stagedCopy!, 'staged')}
                             className="p-1.5 hover:bg-secondary/10 rounded-lg text-muted-foreground hover:text-secondary transition-all"
                          >
                            {copied === 'staged' ? <CheckCircle className="w-4 h-4 text-secondary" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    )}
                 </div>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <div className="space-y-1">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{resources.assetDetail.assetId}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-mono text-muted-foreground break-all">{asset.id}</p>
                    </div>
                 </div>
                 <div className="space-y-1">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{resources.assetDetail.bucket}</p>
                    <p className="text-sm font-medium text-foreground">{asset.locationAddress}</p>
                 </div>
               </div>
             </div>
          </div>

          {/* Actions */}
          <div className="bg-card rounded-3xl border border-border shadow-sm overflow-hidden">
            <div className="p-8 space-y-4">
              <h3 className="text-sm font-bold text-foreground uppercase tracking-widest">Actions</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => handleAction('reingest')}
                  disabled={actionState === 'loading'}
                  className="flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-xl font-semibold hover:opacity-90 transition-all text-sm shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionState === 'loading' ? 'Re-ingesting…' : 'Re-ingest'}
                </button>
              </div>
              {actionState === 'success' && (
                <p className="text-sm text-secondary font-medium">{actionMessage}</p>
              )}
              {actionState === 'error' && (
                <p className="text-sm text-destructive font-medium">{actionMessage}</p>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Timeline */}
        <div className="lg:w-1/3">
           <div className="bg-card border border-border rounded-3xl shadow-sm p-8 sticky top-8">
              <h2 className="text-lg font-bold text-foreground mb-8 flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                {resources.assetDetail.processingHistory}
              </h2>

              <div className="relative space-y-8">
                {/* Timeline Line */}
                <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-muted"></div>

                {asset.events.map((event, idx) => {
                  const label = isStateChangeEvent(event) ? event.state : `Action: ${event.actionType}`;
                  const isActive = isStateChangeEvent(event) && event.state === asset.state;
                  return (
                    <div key={idx} className="relative flex gap-4">
                      <div className={`
                        relative z-10 w-6 h-6 rounded-full border-4 border-card shadow-sm shrink-0 flex items-center justify-center
                        ${isActive ? 'bg-primary ring-4 ring-primary/20' : 'bg-muted'}
                      `}>
                        {isActive && <div className="w-1.5 h-1.5 bg-primary-foreground rounded-full"></div>}
                      </div>
                      <div className="space-y-1">
                        <p className={`text-sm font-bold ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {label}
                        </p>
                        <p className="text-xs text-muted-foreground">{formatDate(event.date)}</p>
                        {isStateChangeEvent(event) && event.description && (
                          <p className="text-sm text-muted-foreground leading-relaxed mt-2 p-3 bg-muted rounded-xl border border-border">
                            {event.description}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 pt-8 border-t border-border">
                <div className="flex items-center gap-3 p-4 bg-muted rounded-2xl border border-border">
                  <Info className="w-5 h-5 text-muted-foreground shrink-0" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {resources.assetDetail.footerInfo(asset.state)}
                  </p>
                </div>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};
